// Core service implementation
import {
  Keyframe,
  CreateKeyframeData,
  UpdateKeyframeData,
  KeyframeRendererProvider,
  KeyframeDatabaseProvider,
  SavedKeyframeRow
} from './types';
import { parseVector3String, formatVector3String } from '@/utils/numberUtils';
import { keyframeRowOf } from './keyframeRows';
import { FrameCoalescer } from '@/common/frameCoalescer';
import { confirmedAs, deletedHere, newerEditThan, poseStated, savingKeyframes } from './localEdits';
import { allowedForKeyframe, categoryOfAsset, placedKeyframeTransform } from './anchorKeyframes';

import type { WriteScope } from '@/modules/undoRedo';
/** How an update reaches the undo stack. */
export interface KeyframeUpdateOptions {
  /** The gesture the write belongs to, or untracked. */
  scope?: WriteScope;
}

// Database stores times with 5 decimal places (e.g., 4.89688)
const TIME_PRECISION = 100000;
const roundTime = (time: number) => Math.round(time * TIME_PRECISION) / TIME_PRECISION;

export class KeyframeServiceImpl {
  private heldKeyframe: (keyframeId: string) => Keyframe | undefined = () => undefined;

  followEditor(held: (keyframeId: string) => Keyframe | undefined): void {
    this.heldKeyframe = held;
  }

  private readonly preview = new FrameCoalescer<Keyframe>((keyframe) => this.sendKeyframeToRenderer(keyframe));

  constructor(
    private readonly keyframeService: KeyframeDatabaseProvider,
    private readonly renderer: KeyframeRendererProvider
  ) {}

  async fetchKeyframesForAsset(assetInstanceId: string) {
    return this.keyframeService.fetchKeyframesForAsset(assetInstanceId);
  }

  async fetchKeyframesForAssets(assetInstanceIds: string[]) {
    return this.keyframeService.fetchKeyframesForAssets(assetInstanceIds);
  }

  async fetchKeyframeRow(keyframeId: string) {
    return this.keyframeService.fetchKeyframeRow(keyframeId);
  }

  async addKeyframeRow(newKeyframe: CreateKeyframeData, scope?: WriteScope) {
    return this.keyframeService.addKeyframeRow(newKeyframe, scope);
  }

  async updateKeyframeRow(keyframeId: string, updateData: UpdateKeyframeData, scope?: WriteScope) {
    return this.keyframeService.updateKeyframeRow(keyframeId, updateData, scope);
  }

  async deleteKeyframeRow(keyframeId: string, scope?: WriteScope) {
    return this.keyframeService.deleteKeyframeRow(keyframeId, scope);
  }

  // Helper method to convert position/rotation/scale objects to string format
  private convertVectorToString(vector: { x: number; y: number; z: number }): string {
    return formatVector3String(vector, 3);
  }

  // Helper method to convert string format back to vector object
  private convertStringToVector(vectorString: string): { x: number; y: number; z: number } {
    const parsed = parseVector3String(vectorString);
    if (!parsed) {
      console.error('Failed to parse vector string, using defaults:', vectorString);
      return { x: 0, y: 0, z: 0 }; // Safe default
    }
    return parsed;
  }

  // Business logic method to create a keyframe with proper data conversion
  async createKeyframe(
    assetInstanceId: string,
    timePosition: number,
    position: { x: number; y: number; z: number },
    rotation: { x: number; y: number; z: number },
    scale: { x: number; y: number; z: number },
    category: string = 'middle',
    sceneInstanceId: string,
    keyframeId?: string,
    scope?: WriteScope
  ) {
    ({ position, rotation, scale } = placedKeyframeTransform(categoryOfAsset(assetInstanceId), { position, rotation, scale }));
    const keyframeData: CreateKeyframeData = {
      asset_instance_id: assetInstanceId,
      keyframe_time: timePosition,
      keyframe_position: this.convertVectorToString(position),
      keyframe_rotation: this.convertVectorToString(rotation),
      keyframe_scale: this.convertVectorToString(scale),
      keyframe_category: category,
      scene_instance_id: sceneInstanceId
    };

    // Include the keyframe_id if provided
    if (keyframeId) {
      keyframeData.keyframe_id = keyframeId;
      poseStated({ id: keyframeId, assetInstanceId, sceneInstanceId, timePosition, position, rotation, scale, category });
    }

    const { result, tick } = await savingKeyframes(keyframeId ? [keyframeId] : [], () => this.addKeyframeRow(keyframeData, scope));
    if (result[0]) confirmedAs(this.rowToKeyframe(result[0]), tick);
    this.renderer.keyframeAdded(result[0]);
    return result;
  }

  // Create default start keyframe for a new asset
  async createDefaultKeyframesForAsset(
    assetInstanceId: string,
    assetBeginTime: number,
    assetEndTime: number,
    sceneInstanceId: string,
    defaultPosition: { x: number; y: number; z: number } = { x: 0, y: 0, z: 0 },
    defaultRotation: { x: number; y: number; z: number } = { x: 0, y: 0, z: 0 },
    defaultScale: { x: number; y: number; z: number } = { x: 1, y: 1, z: 1 },
    scope?: WriteScope
  ) {
    try {
      // Create start keyframe at the beginning of the asset
      const startKeyframe = await this.createKeyframe(
        assetInstanceId,
        assetBeginTime,
        defaultPosition,
        defaultRotation,
        defaultScale,
        'start',
        sceneInstanceId,
        undefined,
        scope
      );

      return [startKeyframe];
    } catch (error) {
      console.error('Error creating default keyframes for asset:', error);
      throw error;
    }
  }

  // Business logic method to update a keyframe with proper data conversion.
  // Callers SHOULD pass `previousKeyframe` (the state before this update) so the
  // diff can be built without an extra DB round-trip.
  // Omit it only as a fallback; the service will fetch and log a warning.
  async updateKeyframe(
    keyframeId: string,
    timePosition?: number,
    position?: { x: number; y: number; z: number },
    rotation?: { x: number; y: number; z: number },
    scale?: { x: number; y: number; z: number },
    category?: string,
    previousKeyframe?: Keyframe | null,
    options: KeyframeUpdateOptions = {}
  ) {
    // `undefined` = caller didn't supply, fall back to DB; `null` = caller asserts no prior state.
    const previous: Keyframe | null = previousKeyframe !== undefined
      ? previousKeyframe
      : await this.resolvePreviousKeyframe(keyframeId);

    ({ position, rotation, scale } = allowedForKeyframe(keyframeId, { position, rotation, scale }));

    const updateData: UpdateKeyframeData = {};

    // Only update timePosition if it's provided AND different from existing
    if (timePosition !== undefined) {
      const roundedNewTime = roundTime(timePosition);
      const existingTime = previous?.timePosition;
      const roundedExistingTime = existingTime !== undefined ? roundTime(existingTime) : undefined;

      if (roundedExistingTime === undefined || roundedExistingTime !== roundedNewTime) {
        updateData.keyframe_time = roundedNewTime;
      }
    }

    // Only update position if it's provided AND different from existing
    if (position !== undefined) {
      const existingPosition = previous?.position;
      if (!existingPosition ||
          existingPosition.x !== position.x ||
          existingPosition.y !== position.y ||
          existingPosition.z !== position.z) {
        updateData.keyframe_position = this.convertVectorToString(position);
      }
    }

    // Only update rotation if it's provided AND different from existing
    if (rotation !== undefined) {
      const existingRotation = previous?.rotation;
      if (!existingRotation ||
          existingRotation.x !== rotation.x ||
          existingRotation.y !== rotation.y ||
          existingRotation.z !== rotation.z) {
        updateData.keyframe_rotation = this.convertVectorToString(rotation);
      }
    }

    // Only update scale if it's provided AND different from existing
    if (scale !== undefined) {
      const existingScale = previous?.scale;
      if (!existingScale ||
          existingScale.x !== scale.x ||
          existingScale.y !== scale.y ||
          existingScale.z !== scale.z) {
        updateData.keyframe_scale = this.convertVectorToString(scale);
      }
    }

    // Only update category if it's provided AND different from existing (a drag passes the keyframe's own)
    if (category !== undefined && category !== previous?.category) {
      updateData.keyframe_category = category;
    }

    if (Object.keys(updateData).length === 0) {
      return [];
    }

    const stating = previous ?? this.heldKeyframe(keyframeId);
    if (stating) {
      poseStated({
        ...stating,
        timePosition: timePosition ?? stating.timePosition,
        position: position ?? stating.position,
        rotation: rotation ?? stating.rotation,
        scale: scale ?? stating.scale,
      });
    }

    const { result, tick } = await savingKeyframes([keyframeId], () => this.updateKeyframeRow(keyframeId, updateData, options.scope));
    if (result[0]) confirmedAs(this.rowToKeyframe(result[0]), tick);
    // The editor's keyframe, not the reply: the row may hold a renderer placement saved since
    if (!newerEditThan(keyframeId, tick)) {
      const held = this.heldKeyframe(keyframeId);
      if (held) this.sendKeyframeToRenderer(held);
      else this.renderer.keyframeUpdated(result[0]);
    }
    return result;
  }

  private rowToKeyframe(row: SavedKeyframeRow): Keyframe {
    return {
      id: row.keyframe_id,
      assetInstanceId: row.asset_instance_id,
      sceneInstanceId: row.scene_instance_id as string,
      timePosition: row.keyframe_time as number,
      position: row.keyframe_position ? this.convertStringToVector(row.keyframe_position as string) : undefined,
      rotation: row.keyframe_rotation ? this.convertStringToVector(row.keyframe_rotation as string) : undefined,
      scale: row.keyframe_scale ? this.convertStringToVector(row.keyframe_scale as string) : undefined,
      category: row.keyframe_category as string | undefined,
      createdAt: row.created_at as string | undefined,
    };
  }

  private async resolvePreviousKeyframe(keyframeId: string): Promise<Keyframe | null> {
    try {
      const row = await this.fetchKeyframeRow(keyframeId);
      if (!row) return null;
      return this.rowToKeyframe(row);
    } catch (error) {
      // Caller didn't supply previous state, so we hit the DB; log instead of swallowing so
      // Sentry/console picks it up. Update proceeds without diff (writes whatever was passed).
      console.warn('[KeyframeService.updateKeyframe] Failed to fetch existing keyframe; proceeding without diff:', error);
      return null;
    }
  }

  // Delete a keyframe; once it is gone reads skip it and the renderer drops it
  async deleteKeyframe(keyframeId: string, assetInstanceId: string, scope?: WriteScope) {
    const { result } = await savingKeyframes([keyframeId], () => this.deleteKeyframeRow(keyframeId, scope));
    deletedHere(keyframeId);
    this.renderer.keyframeRemoved(keyframeId, assetInstanceId);
    return result;
  }

  // Select keyframe in the renderer's viewport
  selectKeyframe(keyframe: Keyframe) {
    this.renderer.selectKeyframe(keyframe.id, keyframe.assetInstanceId);
  }

  // Send a keyframe to the renderer (without database update) - used for syncing existing keyframes
  sendKeyframeToRenderer(keyframe: Keyframe) {
    this.renderer.keyframeUpdated(keyframeRowOf(keyframe));
  }

  // A draft of a keyframe while a control is dragged: the renderer sees it once a frame, nothing is saved
  previewKeyframeInRenderer(keyframe: Keyframe, frameWindow?: Window) {
    this.preview.schedule(keyframe, frameWindow);
  }

  endKeyframePreview() {
    this.preview.cancel();
  }

  // Tell the renderer a keyframe is gone (used for syncing deletions found in the database)
  sendKeyframeRemovedToRenderer(keyframeId: string, assetInstanceId: string) {
    this.renderer.keyframeRemoved(keyframeId, assetInstanceId);
  }
}
